/* EntityTreeGridComponent.js */

"use strict";

const EventEmitter = require('events').EventEmitter;
const Renderer = require("./EntityTreeGridRenderer");
const _ = require('lodash');

class EntityTreeGridComponent extends EventEmitter {


    constructor(configOptions) {
        super();

        this._id = _.get(configOptions, 'id', "entity_tree_grid_gl_" + Date.now());
        this._columns = _.get(configOptions, 'columns', {});
        this._columnOrder = _.get(configOptions, 'columnOrder', []);
        if (this._columnOrder.length === 0) {
            for (let colId in this._columns) {
                this._columnOrder.push(colId);
            }
        }
        this._selectedRows = _.get(configOptions, 'selectedRows', []);
        this._singleSelect = _.get(configOptions, 'singleSelect', false);

        this._changeFeed = _.get(configOptions, 'changeFeed');

        this._entities = {};
        this._entityOrder = [];

        this._renderer = new Renderer(this);

        this._registerListeners();
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners(){
        this._changeFeed.on('ChangeFeedInitialized', this._onChangeFeedInitialized = this._onChangeFeedInitialized.bind(this));
        this._changeFeed.on('ChangeFeedStarted', this._onChangeFeedStarted = this._onChangeFeedStarted.bind(this));
        this._changeFeed.on('StatusChanged', this._onStatusChanged = this._onStatusChanged.bind(this));
        this._changeFeed.on('EntityAdded', this._onEntityAdded = this._onEntityAdded.bind(this));
        this._changeFeed.on('EntityPropertiesUpdated', this._onEntityPropertiesUpdated = this._onEntityPropertiesUpdated.bind(this));
        this._changeFeed.on('EntityRemoved', this._onEntityRemoved = this._onEntityRemoved.bind(this));
        this._changeFeed.on('ShutdownStarted', this._onShutdownStarted = this._onShutdownStarted.bind(this));
        this._changeFeed.on('ShutdownComplete', this._onShutdownComplete = this._onShutdownComplete.bind(this));
    }
    _unregisterListeners(){
        this._changeFeed.removeListener('ChangeFeedInitialized', this._onChangeFeedInitialized);
        this._changeFeed.removeListener('ChangeFeedStarted', this._onChangeFeedStarted);
        this._changeFeed.removeListener('StatusChanged', this._onStatusChanged);
        this._changeFeed.removeListener('EntityAdded', this._onEntityAdded);
        this._changeFeed.removeListener('EntityPropertiesUpdated', this._onEntityPropertiesUpdated);
        this._changeFeed.removeListener('EntityRemoved', this._onEntityRemoved);
        this._changeFeed.removeListener('ShutdownStarted', this._onShutdownStarted);
        this._changeFeed.removeListener('ShutdownComplete', this._onShutdownComplete);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get renderer(){
        return this.getRenderer();
    }
    getRenderer(){
        return this._renderer;
    }
    shutdown(){
        this._unregisterListeners();
        return this._renderer._shutdown().then(() => {
            this._renderer = undefined;

            this._id = undefined;
            this._entities = undefined;
            this._columns = undefined;
            this._columnOrder = undefined;
            this._selectedRows = undefined;
            this._singleSelect = undefined;
            this._entityOrder = undefined;

            // TODO: Imagine there will be shutdown logic necessary here
            this._changeFeed = undefined;
        });
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * @private - Fired when the content being managed by the change feed has been initialized.
     * @param {ChangeFeedCache} cache - An object that represents the current cache of Entities within the Feed. It is key'd by the Entity's id.
     * @param {EntityChangeFeed} changeFeed - This change feed.
     */
    _onChangeFeedInitialized(cache, changeFeed){
        let sortedEntites = changeFeed.getCacheAsArraySorted('fullDisplayPath');
        this._setEntities(sortedEntites);
    }

    /**
     * @private - Fired when the change feed has been established with the Application Server.
     * @param {EntityChangeFeed} changeFeed - This change feed.
     */
    _onChangeFeedStarted(/*changeFeed*/){

    }

    /**
     * @private - Fired when the status of the change feed has changed.
     */
    _onStatusChanged(){

    }

    /**
     * @private - Fired when an Entity has been created or altered in the system and as a
     * result now belongs to this change feed.
     * @param {Entity} entity - The Entity that was added.
     * @param {Object.<string,*> properties - The current properties of this entity.
     * @param {EntityChangeFeed} changeFeed - This change feed.
     */
    _onEntityAdded(entity /*, properties, changeFeed*/){
        this._addEntity(entity);
    }

    /**
     * @private - Fired when an Entity's properties have been updated in the system.
     * @param {Entity} entity - The Entity that was updated.
     * @param {JsonPatch} patches - The Json Patch describing the changes.
     * @param {Object.<string,*>} beforePristineProperties - The raw properties before the update.
     * @param {EntityChangeFeed} changeFeed - This change feed.
     */
    _onEntityPropertiesUpdated(/*entity, patches, beforePristineProperties, changeFeed*/){
        // TODO: Come back to once server-side sorting is flushed out.
        // this._updateEntity(entity);
    }

    /**
     * @private - Fired when an Entity has been removed in the system and as a result no longer belongs to this change feed.
     * @param {Entity} entity - The Entity that was removed.
     * @param {string} cause - The cause the entity being removed from this Change Feed.
     * @param {EntityChangeFeed} changeFeed - This change feed.
     */
    _onEntityRemoved(entity /*, cause, changeFeed*/){
        this._deleteEntity(entity);
    }

    /**
     * @private - Fired when this ChangeFeed starts the shutdown process.
     */
    _onShutdownStarted(){

    }

    /**
     * @private - Fired when this ChangeFeed finishes the shutdown process.
     */
    _onShutdownComplete(){

    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    _triggerUserSelectedRows(selectedRowKeys){
        this.emit('UserSelectedRows', selectedRowKeys);
    }

    _triggerUserRowDoubleClicked(selectedRowKey){
        this.emit('UserRowDoubleClicked', selectedRowKey);
    }

    _triggerUserSorted(columnKey, sorted){
        this.emit('UserSorted', columnKey, sorted);
    }

    _triggerGridAction(eventData){
        this.emit('GridAction', eventData);
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    setId(newVal){
        this._id = newVal;
        this.emit('Id', this._id);
        return this;
    }
    getId(){
        return this._id;
    }

    getEntities(){
        return this._entities;
    }

    getEntity(key){
        return this._entities[key];
    }

    getEntityOrder(){
        return this._entityOrder;
    }

    setColumns(newVal){
        this._columns = newVal;
        this.emit('Columns', this._columns);
        return this;
    }
    getColumns(){
        return this._columns;
    }

    setColumnOrder(newVal){
        this._columnOrder = newVal;
        this.emit('ColumnOrder', this._columnOrder);
        return this;
    }
    getColumnOrder(){
        return this._columnOrder;
    }

    setSelectedRows(rowIdArray){
        this._selectedRows = rowIdArray;
        this.emit('SelectedRows', this._selectedRows);
        return this;
    }
    getSelectedRows(){
        return this._selectedRows;
    }
    addSelectedRow(rowId){
        this._selectedRows.push(rowId);
        this.setSelectedRows(this._selectedRows);
    }
    removeSelectedRow(rowId){
        let rowIndex = this._selectedRows.indexOf(rowId);
        this._selectedRows.splice(rowIndex, 1);
        this.setSelectedRows(this._selectedRows);
    }

    setSingleSelect(newVal){
        this._singleSelect = newVal;
        return this;
    }
    getSingleSelect(){
        return this._singleSelect;
    }
    
    
    /*******************************************/
    /* END PUBLIC API METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _setEntities(newVal){
        let newSelectedRows = [],
            selectedRows = this.getSelectedRows(),
            entities = {};
        this._entityOrder = [];
        newVal.forEach((entity) => {
            let id = entity.getId();
            this._entityOrder.push(id);
            entities[id] = entity;
        });
        this._entities = entities;
        this.emit('Entities', this._entities);
        selectedRows.forEach((row) => {
            if (!_.isUndefined(this._entities[row])) {
                newSelectedRows.push(row);
            }
        });
        this.setSelectedRows(newSelectedRows);
        return this;
    }

    _addEntity(newEntity){
        let entityId = newEntity.getId();
        this._entities[entityId] = newEntity;

        // TODO: Need to properly update entity order!!!  Come back to once serve-side sorting is flushed out.
        this._entityOrder.push(entityId);

        this.emit('AddEntity', newEntity);
        return this;
    }

    // _updateEntity(updatedEntity){
    //
    //     // TODO: Logic needed to figure out if a rip/replace in the DOM is necessary?  ...and update entity order.  Come back to once serve-side sorting is flushed out.
    //
    //     // let entityId = entityObj.entity.getProperty('name'),
    //     //     entity = entityObj.entity;
    //     // this._entities[entityId].entity = entity;
    //     // this.emit('UpdateEntity', entityObj);
    //     // return this;
    // }

    _deleteEntity(entity){
        let selectedRows = this.getSelectedRows(),
            newSelectedRows = [],
            selectedRowFound = false,
            entityId = entity.getId();
        this._entities = _.omit(this._entities, entityId);

        // TODO: Need to properly update entity order!!!  Come back to once serve-side sorting is flushed out.
        this._entityOrder.splice(this._entityOrder.indexOf(entityId), 1);

        selectedRows.forEach((row) => {
            if (row === entityId) {
                selectedRowFound = true;
            } else {
                newSelectedRows.push(row);
            }
        });
        if (selectedRowFound === true) {
            this.setSelectedRows(newSelectedRows);
        }
        this.emit('DeleteEntity', entityId);
        return this;
    }

    _setEntityOrder(newEntityOrder){
        this._entityOrder = newEntityOrder;
        return this;
    }

    /*******************************************/
    /* END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = EntityTreeGridComponent;